import { FC, useCallback } from 'react';
import { Box, Checkbox, IconButton, Stack, Typography } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import type {
  Candidate,
  Decision,
  DecisionPayload,
  Proposal,
} from 'src/services/DataVaultTransfer.service';

interface TransferScalarProposalProps {
  proposal: Proposal;
  decision: Decision | undefined;
  onSave: (payload: DecisionPayload) => void;
}

/** Small discard "x" button for auto-confirmed proposals */
const DiscardButton: FC<{ onClick: () => void }> = ({ onClick }) => (
  <IconButton
    onClick={onClick}
    size="small"
    aria-label="Discard"
    sx={{
      width: 28,
      height: 28,
      color: 'text.disabled',
      '&:hover': {
        bgcolor: 'error.50',
        color: 'error.main',
      },
    }}
  >
    <CloseIcon sx={{ fontSize: 16 }} />
  </IconButton>
);

/** Small undo/re-confirm button for discarded proposals */
const UndoButton: FC<{ onClick: () => void }> = ({ onClick }) => (
  <IconButton
    onClick={onClick}
    size="small"
    aria-label="Undo discard"
    sx={{
      width: 28,
      height: 28,
      color: 'text.disabled',
      '&:hover': {
        bgcolor: 'success.50',
        color: 'success.main',
      },
    }}
  >
    <CheckIcon sx={{ fontSize: 16 }} />
  </IconButton>
);

const TransferScalarProposal: FC<TransferScalarProposalProps> = ({
  proposal,
  decision,
  onSave,
}) => {
  const {
    qit_id,
    instance_index,
    question_label,
    candidates,
    is_unanimous,
    is_single_source,
  } = proposal;
  const currentDecision = decision?.decision;

  // Match using full source tuple (survey_id + qit_id + answer_group_id) to correctly
  // identify the selected candidate. Using qit_id alone fails because it's the question
  // template ID which is shared across all source certifications.
  const selectedCandidateIdx =
    decision?.chosen_source_survey_id != null
      ? candidates?.findIndex((c) =>
          c.sources.some(
            (s) =>
              s.survey_id === decision.chosen_source_survey_id &&
              s.qit_id === decision.chosen_source_qit_id &&
              (s.answer_group_id ?? null) ===
                (decision.chosen_source_answer_group_id ?? null)
          )
        ) ?? -1
      : -1;

  const handleConfirm = useCallback(() => {
    if (!candidates || candidates.length === 0) {
      return;
    }
    const source = candidates[0].sources[0];
    onSave({
      qit_id,
      instance_index,
      decision: 'confirm',
      chosen_source_survey_id: source.survey_id,
      chosen_source_qit_id: source.qit_id,
      chosen_source_answer_group_id: source.answer_group_id,
    });
  }, [qit_id, instance_index, candidates, onSave]);

  const handleDiscard = useCallback(() => {
    onSave({ qit_id, instance_index, decision: 'discard' });
  }, [qit_id, instance_index, onSave]);

  const handleToggleCandidate = useCallback(
    (candidate: Candidate, idx: number) => {
      if (idx === selectedCandidateIdx) {
        // Deselect the current selection → discard
        onSave({ qit_id, instance_index, decision: 'discard' });
      } else {
        // Select this candidate
        const source = candidate.sources[0];
        onSave({
          qit_id,
          instance_index,
          decision: 'pick',
          chosen_source_survey_id: source.survey_id,
          chosen_source_qit_id: source.qit_id,
          chosen_source_answer_group_id: source.answer_group_id,
        });
      }
    },
    [qit_id, instance_index, selectedCandidateIdx, onSave]
  );

  // Unanimous or single-source: auto-confirmed by default with "x" to discard
  if (is_unanimous || is_single_source) {
    const value = candidates?.[0]?.display_value || '';
    const isConfirmed = currentDecision === 'confirm' || !currentDecision;
    const isDiscarded = currentDecision === 'discard';

    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          px: 2,
          pl: 5.25,
          py: 1.5,
          borderBottom: '1px solid',
          borderColor: 'grey.100',
          borderLeft: isConfirmed ? '3px solid' : 'none',
          borderLeftColor: isConfirmed ? 'success.light' : undefined,
          bgcolor: isDiscarded ? 'rgba(239, 83, 80, 0.03)' : 'background.paper',
          opacity: isDiscarded ? 0.6 : 1,
          transition: 'background-color 0.2s, opacity 0.2s',
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0, mr: 1.5 }}>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ fontSize: '0.6875rem' }}
          >
            {question_label}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              fontWeight: 500,
              mt: 0.25,
              textDecoration: isDiscarded ? 'line-through' : 'none',
              color: isDiscarded ? 'text.disabled' : 'text.primary',
            }}
          >
            {value || '(empty)'}
          </Typography>
        </Box>

        <Box sx={{ flexShrink: 0, display: 'flex', gap: 0.75 }}>
          {isDiscarded ? (
            <UndoButton onClick={handleConfirm} />
          ) : (
            <DiscardButton onClick={handleDiscard} />
          )}
        </Box>
      </Box>
    );
  }

  // Multiple candidates: checkbox picker (single-select, deselect = discard)
  const isPicked = currentDecision === 'pick' && selectedCandidateIdx >= 0;
  const isDiscarded = currentDecision === 'discard';

  return (
    <Box
      sx={{
        px: 2,
        pl: 5.25,
        py: 1.5,
        borderBottom: '1px solid',
        borderColor: 'grey.100',
        borderLeft: isPicked ? '3px solid' : 'none',
        borderLeftColor: isPicked ? 'success.light' : undefined,
        bgcolor: isDiscarded ? 'rgba(239, 83, 80, 0.03)' : 'background.paper',
        opacity: isDiscarded ? 0.6 : 1,
        transition: 'background-color 0.2s, opacity 0.2s, border-color 0.2s',
      }}
    >
      <Box sx={{ mb: 1 }}>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ fontSize: '0.6875rem' }}
        >
          {question_label}
        </Typography>
        <Box
          sx={{
            display: 'inline-block',
            ml: 1,
            px: 1,
            py: 0.25,
            borderRadius: 1,
            bgcolor: 'warning.50',
            fontSize: '0.625rem',
            color: 'warning.dark',
          }}
        >
          Multiple answers — pick one
        </Box>
      </Box>

      <Stack spacing={0.75}>
        {candidates?.map((candidate, idx) => {
          const isSelected = idx === selectedCandidateIdx;
          return (
            <Box
              key={idx}
              onClick={() => handleToggleCandidate(candidate, idx)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                px: 1.25,
                py: 1,
                border: '1.5px solid',
                borderColor: isSelected ? 'primary.main' : 'grey.200',
                borderRadius: 2,
                bgcolor: isSelected ? 'primary.50' : 'background.paper',
                cursor: 'pointer',
                transition: 'border-color 0.15s, background-color 0.15s',
                '&:hover': { borderColor: 'primary.light' },
              }}
            >
              <Checkbox
                checked={isSelected}
                size="small"
                sx={{ p: 0 }}
                inputProps={{
                  'aria-label': candidate.display_value || '(empty)',
                }}
              />
              <Box>
                <Typography variant="body2" fontWeight={500}>
                  {candidate.display_value || '(empty)'}
                </Typography>
              </Box>
            </Box>
          );
        })}
      </Stack>
    </Box>
  );
};

export default TransferScalarProposal;
